// #196 — Painel de negócio. Sessões esportivas (solo + downwind + corrida) da
// fonte autoritativa Postgres via REST. Todos os endpoints são gateados por
// `RequireVerifiedOrganizer` no backend (trajeto GPS é PII de qualquer atleta);
// o frontend adiciona AuthGuard requireRole="verified_organizer" como defesa em
// profundidade e pra esconder o link/menu.
//
// verified_organizer É o super-admin do FaroMaps hoje (mesmo gate de /users,
// CSV import, dw-support onboarding). Não há role `admin` separado — decisão
// consciente pro MVP (Felipe, 2026-07-04): reusar o gate existente em vez de
// introduzir uma role nova com claims Firebase a conceder.

import { api, apiUrl, ApiError, getAuthToken } from './client';

/// Tipos de sessão conhecidos. `race`/`support` existem no schema mas o app
/// hoje grava majoritariamente solo/downwind. Mantidos pro filtro cobrir o
/// domínio inteiro sem inventar categoria.
export type SessionType =
  | 'solo'
  | 'downwind_solo'
  | 'downwind_group'
  | 'race'
  | 'support';

/// Rótulos de exibição — SÓ no painel de negócio (admin, verified_organizer).
/// O usuário do app NÃO vê estas strings; os valores das chaves são os do banco,
/// gravados pelo mobile, e não mudam. Taxonomia de produto (Felipe, 2026-07-04):
/// - `solo` = Freeride (fluxo da home)
/// - `downwind_solo` = DW (fluxo B2C — "Montar downwind")
/// - `downwind_group` = DW com Apoio (fluxo B2B — banner amarelo "Com apoio",
///   empresas de guia/resgate). Proxy: o fluxo B2B roda como party de grupo.
/// - `race` = Evento - Atleta (atleta numa prova)
/// - `support` = Evento - Apoio (apoio numa prova)
export const SESSION_TYPE_LABELS: Record<string, string> = {
  solo: 'Freeride',
  downwind_solo: 'DW',
  downwind_group: 'DW com Apoio',
  race: 'Evento - Atleta',
  support: 'Evento - Apoio',
};

/// Linha da listagem paginada (GET /api/sessions). Espelha o
/// `BusinessSessionRow` do backend — não é a entidade inteira. O detalhe
/// (GET /api/sessions/{id}) devolve o mesmo shape sem os campos opcionais
/// de origem e kite point.
export interface SessionSummary {
  id: string;
  userId: string;
  /// Nome do atleta via LEFT JOIN user_profiles no backend. Null quando o
  /// atleta nunca sincronizou perfil — o cliente cai no UID abreviado
  /// (ver `athleteLabel`).
  displayName: string | null;
  sessionType: string;
  category: string;
  /// Origem da captura (#245). Null = celular. Relógio e a shell do servidor
  /// (`server_autocreate`, #638) contam como qualquer sessão; o painel só
  /// mostra uma etiqueta.
  source?: string | null;
  /// Slug do spot (#484) e o rótulo atual dele, via LEFT JOIN kite_points.
  kitePointId?: string | null;
  kitePointName?: string | null;
  partyId: string | null;
  /// UTC ISO. Na listagem (#939) nunca vem null: o servidor exclui sessão
  /// sem `start_time`. O tipo segue nullable porque o detalhe
  /// (GET /api/sessions/{id}) ainda pode devolver uma sessão sem início, e
  /// aí a tela mostra "-".
  startTime: string | null;
  /// UTC ISO ou null enquanto `status === 'ongoing'`.
  endTime: string | null;
  totalDistanceMeters: number;
  maxSpeedKnots: number;
  avgSpeedKnots: number;
  gpsPointsRecorded: number;
  batteryDrainPct: number | null;
  timeToFirstFixMs: number | null;
  bgTransitions: number | null;
  gpsRejectedLowAcc: number | null;
  gpsRejectedTeleport: number | null;
  status: string; // 'ongoing' | 'finished'
}

/// Ponto GPS leve pro replay (GET /api/sessions/{id}/points). Trajeto = PII.
export interface SessionPoint {
  lat: number;
  lng: number;
  /// UTC ISO (device_timestamp).
  ts: string;
  speedKnots: number | null;
  battery: number | null;
}

/// Filtros do painel de negócio (#939). O MESMO conjunto vai pra lista, pro
/// resumo e pro CSV: é o que mantém o "1–50 de N" coerente.
export interface SessionFilters {
  /// UTC ISO, inclusivo.
  since?: string;
  /// UTC ISO, EXCLUSIVO (o "Personalizado" manda o início do dia seguinte).
  until?: string;
  /// Um dos `SESSION_TYPE_LABELS`. Outro valor → 400.
  type?: string;
  /// Slug do catálogo de modalidades. Sessão de evento nunca casa (o
  /// `category` dela é a divisão da prova, #284).
  sport?: string;
  /// `watch` (qualquer relógio standalone), `server` (shell criada pelo
  /// servidor, #638) ou `phone` (todo o resto). Os três somam o total.
  /// Com `server`, os filtros `type`/`sport` nunca casam: tipo e modalidade
  /// dela são default, não declaração.
  source?: string;
  /// Trecho do nome/apelido (sem caixa), e-mail exato (resolvido no Firebase)
  /// ou UID exato.
  athlete?: string;
}

export interface SessionListParams extends SessionFilters {
  /// Cursor opaco devolvido em `nextCursor` da página anterior.
  cursor?: string | null;
  /// 1–100 (default 50). Acima de 100 o servidor corta e responde com o
  /// header `X-Limit-Clamped`.
  limit?: number;
}

/// Página da lista por keyset `(start_time, id)`. `nextCursor` null = última
/// página. O total NÃO vem aqui: vem do resumo, que tem o mesmo filtro.
export interface SessionPage {
  items: SessionSummary[];
  nextCursor: string | null;
  limit: number;
}

/// KPIs agregados no servidor sobre o período inteiro
/// (GET /api/sessions/summary). Período vazio = zeros, nunca 204.
export interface SessionsSummary {
  total: number;
  ongoing: number;
  /// `total - ongoing`.
  finished: number;
  totalDistanceMeters: number;
  /// Só sessões com início e fim (ongoing fora). Null sem nenhuma.
  avgDurationMs: number | null;
  /// Atletas distintos com sessão no período.
  activeAthletes: number;
}

function filterQuery(params: SessionListParams): string {
  const qs = new URLSearchParams();
  if (params.since) qs.set('since', params.since);
  if (params.until) qs.set('until', params.until);
  if (params.type) qs.set('type', params.type);
  if (params.sport) qs.set('sport', params.sport);
  if (params.source) qs.set('source', params.source);
  if (params.athlete) qs.set('athlete', params.athlete);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const sessionsApi = {
  list: (params: SessionListParams = {}): Promise<SessionPage> =>
    api<SessionPage>(`/api/sessions${filterQuery(params)}`),

  summary: (filters: SessionFilters = {}): Promise<SessionsSummary> =>
    api<SessionsSummary>(`/api/sessions/summary${filterQuery(filters)}`),

  /// CSV do período inteiro, gerado em stream no servidor (`;` + BOM pro
  /// Excel em português). Baixa por fetch + blob porque um `<a href>` direto
  /// não levaria o Bearer token, e o endpoint é `verified_organizer`.
  exportCsv: async (filters: SessionFilters = {}): Promise<void> => {
    const token = await getAuthToken();
    const res = await fetch(apiUrl(`/api/sessions/export.csv${filterQuery(filters)}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new ApiError(res.status, res.statusText, await res.text().catch(() => ''));
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const name = /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? 'sessoes.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revogar no mesmo tick pode cancelar o download em alguns navegadores.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },

  /// Detalhe de uma sessão. O backend devolve a entidade RaceSession inteira;
  /// aqui só tipamos o subconjunto que a tela de replay consome (mesmo shape
  /// da listagem serve — os campos coincidem).
  get: (id: string): Promise<SessionSummary> =>
    api<SessionSummary>(`/api/sessions/${encodeURIComponent(id)}`),

  points: (id: string): Promise<SessionPoint[]> =>
    api<SessionPoint[]>(`/api/sessions/${encodeURIComponent(id)}/points`),
};
